import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { RoleKey, homePathFor } from './roles';

interface ProtectedRouteProps {
	children: React.ReactNode;
	roles?: RoleKey[]; // if set, user role must be one of these
}

/**
 * Gate for authenticated routes:
 *   • While the bootstrap refresh is in-flight → render a calm skeleton (NOT
 *     a spinner that flashes for 100ms then disappears).
 *   • Anonymous → bounce to /login with redirect-back state.
 *   • Authenticated but role mismatch → bounce to user's home (we never show
 *     the dreaded "Forbidden" wall; we route them somewhere they can use).
 */
export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
	const { status, user } = useAuth();
	const location = useLocation();

	if (status === 'loading') {
		return (
			<div className="min-h-screen grid place-items-center bg-ground">
				<div className="flex items-center gap-2 text-ink-muted text-sm">
					<svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
					</svg>
					Loading…
				</div>
			</div>
		);
	}

	if (status === 'anonymous' || !user) {
		return <Navigate to="/login" replace state={{ from: location }} />;
	}

	if (roles && roles.length > 0 && (!user.role || !roles.includes(user.role))) {
		return <Navigate to={homePathFor(user.role)} replace />;
	}

	return <>{children}</>;
}

export default ProtectedRoute;
